// ============================================================
// Assign Fee Page
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Tabs, Tab, Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, IconButton, Tooltip, CircularProgress, Paper, FormControl, InputLabel, Select, Checkbox } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Search as SearchIcon, Trash2, Filter, Layers, UserCheck, CheckCircle2, Pencil } from 'lucide-react';
import { FcBusinessman, FcBusinesswoman } from 'react-icons/fc';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useFeeStructures, useAssignableFeeStructures, useStudentFeeAssignments, useAssignStudentFees, useDeleteStudentFeeAssignment } from '@/hooks/useFeeStructure';
import { useSchool } from '@/hooks/useSchool';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import toast from 'react-hot-toast';

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{
        p: 1.5,
        borderBottom: `1px solid ${tokens.colors.border?.main || '#e0e0e0'}`,
        gap: 1.5,
      }}
    >
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export function AssignFeePage() {
  usePageMeta('Assign Fee', [
    { label: 'Fees', path: '/fees' },
    { label: 'Assign' },
  ]);

  const { schoolId } = useSchool();
  const { data: assignableStructures = [], refetch: refetchAssignableStructures } = useAssignableFeeStructures(0, 100);
  const { data: feeStructures = [] } = useFeeStructures();
  const { data: assignments = [], isLoading: isLoadingAssignments } = useStudentFeeAssignments(0, 100);
  const { mutate: assignStudentFees, isPending: isAssigning } = useAssignStudentFees();
  const { mutate: deleteAssignment, isPending: isDeleting } = useDeleteStudentFeeAssignment();

  // ── Navigation Tabs ─────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Master Reference Data ───────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);

  // ── Filter States ───────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedFeeStructures, setSelectedFeeStructures] = useState([]);
  const [remarks, setRemarks] = useState('fee structure is assigned');

  // ── Student Checklist States ────────────────────────────────
  const [students, setStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [feeStatusFilter, setFeeStatusFilter] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  // ── Assigned Fees List Search & Delete Modal ────────────────
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  // ── Single Student Fee Update Modal States ──────────────────
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [studentToUpdate, setStudentToUpdate] = useState(null);
  const [updateFeeStructures, setUpdateFeeStructures] = useState([]);
  const [updateAcademicYear, setUpdateAcademicYear] = useState('');
  const [updateRemarks, setUpdateRemarks] = useState('fee structure is updated');

  // ── Load Sessions & Classes ─────────────────────────────────
  useEffect(() => {
    if (schoolId) {
      // Fetch Academic Sessions
      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          setSessions(list);
          const current = list.find((s) => s.is_current) || list.find((s) => s.status === 'ACTIVE') || list[0];
          if (current?.id) {
            setSelectedYear(current.id);
          }
        })
        .catch((err) => console.error('Failed to load academic sessions:', err));

      // Fetch Classes & Sections
      studentsApi
        .getClassesSections()
        .then((res) => {
          const list = res?.data || (Array.isArray(res) ? res : []);
          setClasses(list);
        })
        .catch((err) => console.error('Failed to load classes & sections:', err));
    }
  }, [schoolId]);

  // Update Available Sections when selectedClass changes
  useEffect(() => {
    if (selectedClass && classes.length > 0) {
      const cls = classes.find((c) => (c.id || c.class_id) === selectedClass);
      setAvailableSections(cls?.sections || []);
    } else {
      setAvailableSections([]);
    }
  }, [selectedClass, classes]);

  // Handle Class Change
  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    setSelectedSection('');
    setSelectedStudentIds([]);
  };

  // Handle Section Change
  const handleSectionChange = (e) => {
    setSelectedSection(e.target.value);
    setSelectedStudentIds([]);
  };

  // ── Fetch Filtered Students ─────────────────────────────────
  const fetchStudents = useCallback(async () => {
    if (!schoolId || !selectedYear || !selectedClass) {
      setStudents([]);
      setSelectedStudentIds([]);
      return;
    }
    setIsLoadingStudents(true);
    try {
      const params = {
        academic_session_id: selectedYear,
        class_id: selectedClass,
        enrollment_status: 'ACTIVE',
        student_status: 'ACTIVE',
      };
      if (selectedSection) {
        params.section_id = selectedSection;
      }
      const res = await studentsApi.getStudents(schoolId, 1, 100, params);
      const list = res?.items || (Array.isArray(res) ? res : []);
      // Strictly show only students whose enrollment status is ACTIVE
      const activeEnrollmentList = list.filter((s) => {
        const enrollStatus = (s.enrollment_status || s.status || s.student_status || '').toUpperCase();
        return enrollStatus === 'ACTIVE';
      });
      setStudents(activeEnrollmentList);
    } catch (err) {
      console.error('Failed to load students:', err);
      toast.error('Failed to load students for selected class.');
      setStudents([]);
    } finally {
      setIsLoadingStudents(false);
    }
  }, [schoolId, selectedYear, selectedClass, selectedSection]);

  useEffect(() => {
    if (selectedYear && selectedClass) {
      fetchStudents();
    } else {
      setStudents([]);
    }
  }, [selectedYear, selectedClass, selectedSection, fetchStudents]);

  // Combine assignable & all fee structures for lookup
  const allFeeStructures = useMemo(() => {
    const map = new Map();
    [...feeStructures, ...assignableStructures].forEach((f) => {
      if (f?.id) map.set(f.id, f);
    });
    return Array.from(map.values());
  }, [feeStructures, assignableStructures]);

  // Selected fee structures details
  const selectedFeeStructureObjs = useMemo(() => {
    return allFeeStructures.filter((f) => selectedFeeStructures.includes(f.id));
  }, [allFeeStructures, selectedFeeStructures]);

  // Set of student IDs who already have an active fee structure assigned
  const assignedStudentIdsSet = useMemo(() => {
    const set = new Set();
    (assignments || []).forEach((item) => {
      const isActive = item.status === 'ACTIVE' || item.status === undefined || item.status === null;
      if (isActive) {
        if (item.student_id) set.add(item.student_id);
        if (item.student?.id) set.add(item.student.id);
      }
    });
    return set;
  }, [assignments]);

  const isStudentFeeAssigned = useCallback(
    (student) => {
      if (student?.fee_assignment?.assigned === true) return true;
      const id = student?.student_id || student?.id;
      if (!id) return false;
      return assignedStudentIdsSet.has(id);
    },
    [assignedStudentIdsSet]
  );

  // Filter students based on search input & fee status
  const filteredStudents = useMemo(() => {
    let list = students;
    if (feeStatusFilter === 'ASSIGNED') {
      list = list.filter((s) => isStudentFeeAssigned(s));
    } else if (feeStatusFilter === 'NOT_ASSIGNED') {
      list = list.filter((s) => !isStudentFeeAssigned(s));
    }
    if (!studentSearch.trim()) return list;
    const q = studentSearch.toLowerCase();
    return list.filter(
      (s) =>
        (s.student_name && s.student_name.toLowerCase().includes(q)) ||
        (s.admission_no && s.admission_no.toLowerCase().includes(q)) ||
        (s.roll_number && String(s.roll_number).includes(q))
    );
  }, [students, studentSearch, feeStatusFilter, isStudentFeeAssigned]);

  // ── Submit Fee Assignment ───────────────────────────────────
  const handleAssignSubmit = (e) => {
    e.preventDefault();
    if (!selectedYear) {
      toast.error('Please select an Academic Year.');
      return;
    }
    if (selectedFeeStructures.length === 0) {
      toast.error('Please choose at least one Fee Structure.');
      return;
    }
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student.');
      return;
    }

    // Ensure we only submit unassigned active students in bulk
    const validStudentIds = selectedStudentIds.filter((id) => {
      if (assignedStudentIdsSet.has(id)) return false;
      const stObj = students.find((s) => (s.student_id || s.id) === id);
      if (!stObj) return false;
      if (stObj?.fee_assignment?.assigned) return false;
      const enrollStatus = (stObj?.enrollment_status || stObj?.status || stObj?.student_status || '').toUpperCase();
      return enrollStatus === 'ACTIVE';
    });
    if (validStudentIds.length === 0) {
      toast.error('Only unassigned active students can be assigned fees in bulk. Use the Edit icon to update assigned students.');
      return;
    }

    const payload = {
      academic_year_id: selectedYear,
      fee_structure_ids: selectedFeeStructures,
      student_ids: validStudentIds,
      remarks: remarks || 'fee structure is assigned',
    };

    assignStudentFees(payload, {
      onSuccess: (res) => {
        toast.success(res?.message || 'Fee structures assigned successfully!');
        setSelectedStudentIds([]);
        setSelectedFeeStructures([]);
        setSelectedClass('');
        setSelectedSection('');
        setStudentSearch('');
        refetchAssignableStructures();
        if (typeof fetchStudents === 'function') {
          fetchStudents();
        }
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to assign fee structures.');
      },
    });
  };

  // ── Assigned Fees List Map & Search ─────────────────────────
  const enrichedAssignments = useMemo(() => {
    return assignments.map((item) => {
      const matchedFee = allFeeStructures.find((f) => f.id === item.fee_structure_id);
      const matchedStudent = students.find(
        (s) => (s.student_id || s.id) === item.student_id
      );

      const st = item.student || matchedStudent || {};
      const fullName = [st.first_name, st.middle_name, st.last_name]
        .filter(Boolean)
        .join(' ') || st.student_name || st.name;
      const enrollment =
        (Array.isArray(st.enrollments)
          ? st.enrollments.find((e) => (e.status || '').toUpperCase() === 'ACTIVE') || st.enrollments[0]
          : st.enrollment) || {};
      const className =
        st.class?.name ||
        enrollment?.school_class?.class_name ||
        enrollment?.class_name ||
        st.class_name ||
        matchedStudent?.class?.name ||
        matchedStudent?.class_name ||
        '-';
      const sectionName =
        st.section?.name ||
        enrollment?.section?.section_name ||
        enrollment?.section_name ||
        st.section_name ||
        matchedStudent?.section?.name ||
        matchedStudent?.section_name ||
        '-';

      return {
        ...item,
        fee_structure_name:
          item.fee_structure?.name ||
          item.fee_structure_name ||
          matchedFee?.name ||
          `Fee Structure (${item.fee_structure_id?.slice(0, 8)}...)`,
        student_display_name:
          fullName ||
          matchedStudent?.student_name ||
          item.student_name ||
          `Student (${item.student_id?.slice(0, 8)}...)`,
        admission_no: st.admission_no || matchedStudent?.admission_no || '-',
        roll_number: enrollment?.roll_number || st.roll_number || matchedStudent?.roll_number || '-',
        class_name: className,
        section_name: sectionName,
        gender: st.gender || matchedStudent?.gender || 'Male',
      };
    });
  }, [assignments, feeStructures, students, allFeeStructures]);

  const filteredAssignments = useMemo(() => {
    if (!assignmentSearch.trim()) return enrichedAssignments;
    const q = assignmentSearch.toLowerCase();
    return enrichedAssignments.filter(
      (item) =>
        (item.fee_structure_name &&
          item.fee_structure_name.toLowerCase().includes(q)) ||
        (item.student_display_name &&
          item.student_display_name.toLowerCase().includes(q)) ||
        (item.remarks && item.remarks.toLowerCase().includes(q))
    );
  }, [enrichedAssignments, assignmentSearch]);

  const handleOpenDeleteModal = (assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteModalOpen(true);
  };

  const handleOpenUpdateModal = (studentOrAssignment) => {
    const studentId = studentOrAssignment?.student_id || studentOrAssignment?.id || studentOrAssignment?.student?.id;
    const studentName =
      studentOrAssignment?.student_name ||
      studentOrAssignment?.student_display_name ||
      studentOrAssignment?.name ||
      'Student';

    let initialStructures = [];
    if (studentOrAssignment?.fee_structure_id) {
      initialStructures = [studentOrAssignment.fee_structure_id];
    } else if (studentOrAssignment?.fee_assignment?.fee_structure_id) {
      initialStructures = [studentOrAssignment.fee_assignment.fee_structure_id];
    } else if (selectedFeeStructures.length > 0) {
      initialStructures = [...selectedFeeStructures];
    }

    setStudentToUpdate({
      ...studentOrAssignment,
      student_id: studentId,
      student_name: studentName,
    });
    setUpdateAcademicYear(selectedYear || sessions[0]?.id || '');
    setUpdateFeeStructures(initialStructures);
    setUpdateRemarks('fee structure updated');
    setUpdateModalOpen(true);
  };

  const handleUpdateStudentFeeSubmit = () => {
    if (!studentToUpdate) return;
    const studentId = studentToUpdate.student_id || studentToUpdate.id || studentToUpdate.student?.id;
    if (!studentId) {
      toast.error('Invalid student selected.');
      return;
    }
    if (!updateAcademicYear) {
      toast.error('Please select an Academic Year.');
      return;
    }
    if (!updateFeeStructures || updateFeeStructures.length === 0) {
      toast.error('Please select at least one Fee Structure.');
      return;
    }

    const payload = {
      academic_year_id: updateAcademicYear,
      fee_structure_ids: updateFeeStructures,
      student_ids: [studentId],
      remarks: updateRemarks || 'fee structure is updated',
    };

    assignStudentFees(payload, {
      onSuccess: (res) => {
        toast.success(res?.message || `Fee structure updated for ${studentToUpdate.student_name} successfully!`);
        setUpdateModalOpen(false);
        setStudentToUpdate(null);
        refetchAssignableStructures();
        if (typeof fetchStudents === 'function') {
          fetchStudents();
        }
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to update fee structure.');
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!assignmentToDelete?.id) return;
    deleteAssignment(assignmentToDelete.id, {
      onSuccess: () => {
        toast.success('Fee assignment revoked successfully.');
        setDeleteModalOpen(false);
        setAssignmentToDelete(null);
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to revoke fee assignment.');
      },
    });
  };

  // Helper to safely extract row object across MUI X DataGrid versions
  const getRow = (value, row) => row || (value && value.row ? value.row : typeof value === 'object' ? value : {});

  // ── Student Checklist Table Columns ─────────────────────────
  const studentColumns = [
    {
      field: 'select',
      headerName: '',
      width: 55,
      sortable: false,
      filterable: false,
      renderHeader: () => {
        const assignableStudents = filteredStudents.filter((s) => {
          const enrollStatus = (s.enrollment_status || s.status || s.student_status || '').toUpperCase();
          return enrollStatus === 'ACTIVE' && !isStudentFeeAssigned(s);
        });
        const assignableIds = assignableStudents.map((s) => s.student_id || s.id);
        const allSelected = assignableIds.length > 0 && assignableIds.every((id) => selectedStudentIds.includes(id));
        const someSelected = assignableIds.some((id) => selectedStudentIds.includes(id)) && !allSelected;
        const allDisabled = assignableIds.length === 0;

        return (
          <Tooltip title={allDisabled ? "All loaded students are either not actively enrolled or already have fee assigned" : "Select all unassigned active students"}>
            <span>
              <Checkbox
                size="small"
                disabled={allDisabled}
                indeterminate={someSelected}
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedStudentIds((prev) => {
                      const set = new Set([...prev, ...assignableIds]);
                      return Array.from(set);
                    });
                  } else {
                    setSelectedStudentIds((prev) => prev.filter((id) => !assignableIds.includes(id)));
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
      renderCell: (params) => {
        const student = getRow(params?.value, params?.row);
        const id = student?.student_id || student?.id;
        const enrollStatus = (student?.enrollment_status || student?.status || student?.student_status || '').toUpperCase();
        const isEnrollmentActive = enrollStatus === 'ACTIVE';
        const isChecked = selectedStudentIds.includes(id);
        const alreadyAssigned = isStudentFeeAssigned(student);
        const structureName = student.fee_assignment?.fee_structure_name;

        if (!isEnrollmentActive) {
          return (
            <Tooltip title={`Enrollment status is ${enrollStatus}. Inactive students cannot be assigned fee.`}>
              <span>
                <Checkbox size="small" checked={false} disabled={true} />
              </span>
            </Tooltip>
          );
        }

        if (alreadyAssigned) {
          return (
            <Tooltip title={structureName ? `Assigned to: ${structureName}. Use Actions (Edit icon) to update structure.` : "Already assigned. Use Actions (Edit icon) to update structure."}>
              <span>
                <Checkbox
                  size="small"
                  checked={true}
                  disabled={true}
                />
              </span>
            </Tooltip>
          );
        }

        return (
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedStudentIds((prev) => [...prev, id]);
              } else {
                setSelectedStudentIds((prev) => prev.filter((item) => item !== id));
              }
            }}
          />
        );
      },
    },
    {
      field: 'student_name',
      headerName: 'Student Name',
      flex: 1.1,
      minWidth: 160,
      valueGetter: (value, row) => getRow(value, row).student_name || 'Unnamed Student',
      renderCell: (params) => {
        const isFemale = String(params?.row?.gender || '').toUpperCase() === 'FEMALE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            {isFemale ? <FcBusinesswoman size={24} /> : <FcBusinessman size={24} />}
            <Typography variant="body2" fontWeight={500} noWrap>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'admission_no',
      headerName: 'Adm. No.',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (value, row) => getRow(value, row).admission_no || '-',
    },
    {
      field: 'roll_number',
      headerName: 'Roll No.',
      flex: 0.45,
      minWidth: 65,
      valueGetter: (value, row) => getRow(value, row).roll_number || '-',
    },
    {
      field: 'class_section',
      headerName: 'Class & Section',
      flex: 0.75,
      minWidth: 110,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        const className = r.class?.name || r.class_name || r.className || null;
        const sectionName = r.section?.name || r.section_name || r.sectionName || null;
        if (!className && !sectionName) return '-';
        return `${className || '-'}${sectionName ? ` (${sectionName})` : ''}`;
      },
    },
    {
      field: 'father_name',
      headerName: 'Father Name',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        return r.parent?.father_name || r.father_name || r.parent?.guardian_name || '-';
      },
    },
    {
      field: 'mobile',
      headerName: 'Phone',
      flex: 0.8,
      minWidth: 105,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        return r.mobile || r.parent?.father_mobile || r.father_mobile || r.parent?.guardian_mobile || '-';
      },
    },
    {
      field: 'enrollment_status',
      headerName: 'Enrollment Status',
      flex: 0.85,
      minWidth: 120,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        return (r.enrollment_status || r.status || r.student_status || 'ACTIVE').toUpperCase();
      },
      renderCell: (params) => {
        const student = getRow(params.value, params.row);
        const status = (student.enrollment_status || student.status || student.student_status || 'ACTIVE').toUpperCase();
        return (
          <StatusBadge
            status={status === 'ACTIVE' ? 'success' : 'error'}
            label={status}
          />
        );
      },
    },
    {
      field: 'fee_assignment',
      headerName: 'Fee Assigned',
      flex: 1.1,
      minWidth: 160,
      valueGetter: (value, row) => {
        const student = getRow(value, row);
        return isStudentFeeAssigned(student) ? 'Assigned' : 'Not Assigned';
      },
      renderCell: (params) => {
        const student = getRow(params.value, params.row);
        const alreadyAssigned = isStudentFeeAssigned(student);
        const structureName = student.fee_assignment?.fee_structure_name;
        const enrollStatus = (student.enrollment_status || student.status || student.student_status || 'ACTIVE').toUpperCase();
        const isEnrollmentActive = enrollStatus === 'ACTIVE';

        if (!isEnrollmentActive) {
          return (
            <StatusBadge
              status="error"
              label={enrollStatus}
            />
          );
        }

        if (alreadyAssigned) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, width: '100%' }}>
              <Chip
                icon={<CheckCircle2 size={13} />}
                label="ASSIGNED"
                size="small"
                color="success"
                sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20, width: 'fit-content' }}
              />
              {structureName && (
                <Typography 
                  variant="caption" 
                  fontWeight={600} 
                  color="text.secondary"
                  sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}
                  title={structureName}
                >
                  {structureName}
                </Typography>
              )}
            </Box>
          );
        }

        return (
          <Chip
            label="NOT ASSIGNED"
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              borderColor: '#f59e0b',
              color: '#d97706',
              backgroundColor: '#fef3c7',
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const student = getRow(params.value, params.row);
        const enrollStatus = (student.enrollment_status || student.status || student.student_status || 'ACTIVE').toUpperCase();
        if (enrollStatus !== 'ACTIVE') return null;
        return (
          <Tooltip title="Update Fee Structure">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUpdateModal(student);
              }}
            >
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  // ── Assigned Fees Table Columns ─────────────────────────────
  const assignmentColumns = [
    {
      field: 'student_display_name',
      headerName: 'Student',
      flex: 1.3,
      minWidth: 200,
      valueGetter: (value, row) => getRow(value, row).student_display_name || 'Student',
      renderCell: (params) => {
        const isFemale = params?.row?.gender === 'Female';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            {isFemale ? <FcBusinesswoman size={24} /> : <FcBusinessman size={24} />}
            <Typography variant="body2" fontWeight={500} noWrap>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'admission_no',
      headerName: 'Adm. No.',
      flex: 1.2,
      minWidth: 180,
      valueGetter: (value, row) => getRow(value, row).admission_no || '-',
    },
    {
      field: 'roll_number',
      headerName: 'Roll No.',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (value, row) => getRow(value, row).roll_number || '-',
    },
    {
      field: 'class_section',
      headerName: 'Class & Section',
      flex: 0.9,
      minWidth: 140,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        const cls = r.class_name && r.class_name !== '-' ? r.class_name : null;
        const sec = r.section_name && r.section_name !== '-' ? ` (${r.section_name})` : '';
        return cls ? `${cls}${sec}` : '-';
      },
    },
    {
      field: 'fee_structure_name',
      headerName: 'Fee Structure',
      flex: 1.4,
      minWidth: 220,
      valueGetter: (value, row) => getRow(value, row).fee_structure_name || '-',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'assigned_date',
      headerName: 'Assigned Date',
      flex: 0.8,
      minWidth: 120,
      valueGetter: (value, row) => {
        const r = getRow(value, row);
        return r.assigned_date ? String(r.assigned_date).split('T')[0] : '-';
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1.2,
      minWidth: 170,
      valueGetter: (value, row) => getRow(value, row).remarks || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (value, row) => getRow(value, row).status || 'ACTIVE',
      renderCell: (params) => (
        <StatusBadge
          status={params.value === 'ACTIVE' ? 'success' : 'error'}
          label={(params.value || 'ACTIVE').toUpperCase()}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Update Fee Structure">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUpdateModal(params.row);
              }}
            >
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Revoke Assignment">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteModal(params.row);
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const selectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 300,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
  };

  return (
    <Box sx={{ pb: 8 }}>
      <PageHeader
        title="Assign Fees"
        subtitle="Filter students by class & section, select an academic year and fee structure, and assign in bulk."
      />

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newVal) => setActiveTab(newVal)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<UserCheck size={18} />}
            iconPosition="start"
            label="Assign Fee Structure"
          />
          <Tab
            icon={<Layers size={18} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Assigned Fees History</span>
                <Chip
                  label={assignments.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    bgcolor: `${tokens.colors.primary.main}15`,
                    color: tokens.colors.primary.main,
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* TAB 0: ASSIGN FEE STRUCTURE */}
      {activeTab === 0 && (
        <form onSubmit={handleAssignSubmit}>
          <Stack spacing={3}>
            {/* 1. Filter & Selection Card */}
            <WidgetContainer>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                1. Select Target Group & Fee Structure
              </Typography>

              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                {/* Academic Year */}
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                  <InputLabel>Academic Year *</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Academic Year *"
                    onChange={(e) => setSelectedYear(e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    {sessions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.session_name || `${s.start_date} to ${s.end_date}`}
                        {s.is_current && ' (Current)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Class Selector */}
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel>Class *</InputLabel>
                  <Select
                    value={selectedClass}
                    label="Class *"
                    onChange={handleClassChange}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="" disabled>
                      Select Class
                    </MenuItem>
                    {classes.map((c) => (
                      <MenuItem key={c.id || c.class_id} value={c.id || c.class_id}>
                        {c.class_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Section Selector */}
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }} disabled={!selectedClass}>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={selectedSection}
                    label="Section"
                    onChange={handleSectionChange}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All Sections</MenuItem>
                    {availableSections.map((sec) => (
                      <MenuItem key={sec.id || sec.section_id} value={sec.id || sec.section_id}>
                        Section {sec.section_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Multi-Select Fee Structure Selector */}
                <FormControl size="small" sx={{ minWidth: 260, flex: 1.5 }}>
                  <InputLabel>Fee Structures *</InputLabel>
                  <Select
                    multiple
                    value={selectedFeeStructures}
                    label="Fee Structures *"
                    onChange={(e) => {
                      const value = e.target.value;
                      const availableList = assignableStructures.length > 0 ? assignableStructures : feeStructures;
                      if (value.includes('SELECT_ALL')) {
                        if (selectedFeeStructures.length === availableList.length) {
                          setSelectedFeeStructures([]);
                        } else {
                          setSelectedFeeStructures(availableList.map((f) => f.id));
                        }
                      } else {
                        setSelectedFeeStructures(typeof value === 'string' ? value.split(',') : value);
                      }
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const matched = allFeeStructures.find((f) => f.id === value);
                          return (
                            <Chip
                              key={value}
                              label={matched?.name || value}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={selectMenuProps}
                  >
                    {((assignableStructures.length > 0 ? assignableStructures : feeStructures).length > 0) && (
                      <MenuItem value="SELECT_ALL">
                        <Checkbox
                          size="small"
                          checked={
                            selectedFeeStructures.length > 0 &&
                            selectedFeeStructures.length ===
                              (assignableStructures.length > 0 ? assignableStructures : feeStructures).length
                          }
                          indeterminate={
                            selectedFeeStructures.length > 0 &&
                            selectedFeeStructures.length <
                              (assignableStructures.length > 0 ? assignableStructures : feeStructures).length
                          }
                        />
                        <Typography variant="body2" fontWeight={700} sx={{ ml: 1 }}>
                          Select All Fee Structures ({(assignableStructures.length > 0 ? assignableStructures : feeStructures).length})
                        </Typography>
                      </MenuItem>
                    )}
                    {(assignableStructures.length > 0 ? assignableStructures : feeStructures).map((fee) => (
                      <MenuItem key={fee.id} value={fee.id}>
                        <Checkbox size="small" checked={selectedFeeStructures.includes(fee.id)} />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {fee.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Remarks Field */}
              <Box sx={{ mt: 2.5 }}>
                <TextField
                  fullWidth
                  label="Remarks"
                  placeholder="Enter assignment remarks..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  size="small"
                />
              </Box>

              {/* Display Selected Fee Structures Info Cards */}
              {selectedFeeStructureObjs.length > 0 && (
                <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Selected Fee Structures ({selectedFeeStructureObjs.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {selectedFeeStructureObjs.map((structure) => (
                      <Paper
                        key={structure.id}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: `${tokens.colors.primary.main}08`,
                          border: `1px solid ${tokens.colors.primary.main}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flex: 1,
                          minWidth: 240,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                            {structure.name}
                          </Typography>
                          {structure.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {structure.description}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={structure.is_active ? 'ACTIVE' : 'INACTIVE'}
                          color={structure.is_active ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 600, height: 20, fontSize: '0.68rem' }}
                        />
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              )}
            </WidgetContainer>

            {/* 2. Students Selection Checklist */}
            <WidgetContainer>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    2. Select Students
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedYear && selectedClass
                      ? `Showing students in selected Class (${filteredStudents.length} available)`
                      : 'Please select an Academic Year and Class to load students.'}
                  </Typography>
                </Box>

                {/* Filters & Search in Student List */}
                {selectedClass && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Fee Status</InputLabel>
                      <Select
                        value={feeStatusFilter}
                        label="Fee Status"
                        onChange={(e) => setFeeStatusFilter(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All Students</em>
                        </MenuItem>
                        <MenuItem value="NOT_ASSIGNED">Not Assigned</MenuItem>
                        <MenuItem value="ASSIGNED">Assigned</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      placeholder="Search name, roll no, adm no..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 260 } }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon size={16} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              {!selectedClass ? (
                <Box
                  sx={{
                    py: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                  }}
                >
                  <Filter
                    size={36}
                    color={tokens.colors.text.secondary}
                    style={{ marginBottom: 12, opacity: 0.5 }}
                  />
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    Select a Class from the filter above to load students
                  </Typography>
                </Box>
              ) : isLoadingStudents ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : filteredStudents.length === 0 ? (
                <Box
                  sx={{
                    py: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No students found matching your criteria.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    autoHeight
                    rows={filteredStudents}
                    columns={studentColumns}
                    getRowId={(row) => row?.student_id || row?.id || row?.admission_no || row?.roll_number}
                    disableRowSelectionOnClick
                    slots={{ toolbar: CustomToolbar }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    }}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '& .MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: `${tokens.colors.primary.main}08`,
                      },
                    }}
                  />
                </Box>
              )}
            </WidgetContainer>

            {/* 3. Sticky Action Bar */}
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                border: `1px solid ${tokens.colors.border?.main || '#e0e0e0'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pl: 1 }}>
                <UserCheck size={28} color={tokens.colors.primary.main} />
                <Chip
                  label={`${selectedStudentIds.length} Students Selected`}
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: '0.9rem', height: 32 }}
                />
                {selectedFeeStructureObjs.length > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Fee Structure{selectedFeeStructureObjs.length > 1 ? 's' : ''}:{' '}
                    <strong>{selectedFeeStructureObjs.map((s) => s.name).join(', ')}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No Fee Structure Selected
                  </Typography>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={
                  isAssigning ||
                  selectedStudentIds.length === 0 ||
                  selectedFeeStructures.length === 0 ||
                  !selectedYear
                }
                startIcon={
                  isAssigning ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CheckCircle2 size={18} />
                  )
                }
                sx={{
                  px: 4,
                  fontWeight: 700,
                }}
              >
                {isAssigning
                  ? 'Assigning Fees...'
                  : `Assign Fees to ${selectedStudentIds.length} ${
                      selectedStudentIds.length === 1 ? 'Student' : 'Students'
                    }`}
              </Button>
            </Paper>
          </Stack>
        </form>
      )}

      {/* TAB 1: ASSIGNED FEES LIST */}
      {activeTab === 1 && (
        <WidgetContainer>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Assigned Fee Structures
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View all existing fee assignments across students.
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search student or fee structure..."
              value={assignmentSearch}
              onChange={(e) => setAssignmentSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {isLoadingAssignments ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredAssignments.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 2,
              }}
            >
              <Layers
                size={36}
                color={tokens.colors.text.secondary}
                style={{ marginBottom: 12, opacity: 0.5 }}
              />
              <Typography variant="body1" color="text.secondary">
                No fee assignments found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              <DataGrid
                autoHeight
                rows={filteredAssignments}
                columns={assignmentColumns}
                getRowId={(row) => row?.id || row?.assignment_id || row?.student_id}
                disableRowSelectionOnClick
                slots={{ toolbar: CustomToolbar }}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: `${tokens.colors.primary.main}08`,
                  },
                }}
              />
            </Box>
          )}
        </WidgetContainer>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke Fee Assignment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to revoke the fee assignment for{' '}
            <strong>{assignmentToDelete?.student_display_name}</strong>?
          </Typography>
          {assignmentToDelete?.fee_structure_name && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: `3px solid ${tokens.colors.error?.main || '#f44336'}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Fee Structure:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {assignmentToDelete.fee_structure_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />
            }
          >
            {isDeleting ? 'Revoking...' : 'Revoke Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Fee Structure for Particular Student Modal */}
      <Dialog
        open={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Update Fee Structure for {studentToUpdate?.student_name}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {studentToUpdate && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {studentToUpdate.student_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {studentToUpdate.admission_no ? `Adm No: ${studentToUpdate.admission_no} | ` : ''}
                  Class: {studentToUpdate.class_name || studentToUpdate.class?.name || '-'}
                </Typography>
              </Box>
            )}

            {/* Academic Year */}
            <FormControl size="small" fullWidth>
              <InputLabel>Academic Year *</InputLabel>
              <Select
                value={updateAcademicYear}
                label="Academic Year *"
                onChange={(e) => setUpdateAcademicYear(e.target.value)}
                MenuProps={selectMenuProps}
              >
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.session_name || `${s.start_date} to ${s.end_date}`}
                    {s.is_current && ' (Current)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Fee Structure selection */}
            <FormControl size="small" fullWidth>
              <InputLabel>Fee Structures *</InputLabel>
              <Select
                multiple
                value={updateFeeStructures}
                label="Fee Structures *"
                onChange={(e) => {
                  const val = e.target.value;
                  setUpdateFeeStructures(typeof val === 'string' ? val.split(',') : val);
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                      const matched = allFeeStructures.find((f) => f.id === val);
                      return (
                        <Chip
                          key={val}
                          label={matched?.name || val}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={selectMenuProps}
              >
                {(assignableStructures.length > 0 ? assignableStructures : feeStructures).map((fee) => (
                  <MenuItem key={fee.id} value={fee.id}>
                    <Checkbox size="small" checked={updateFeeStructures.includes(fee.id)} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {fee.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Remarks */}
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              value={updateRemarks}
              onChange={(e) => setUpdateRemarks(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUpdateModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStudentFeeSubmit}
            color="primary"
            variant="contained"
            disabled={isAssigning || !updateAcademicYear || updateFeeStructures.length === 0}
            startIcon={
              isAssigning ? <CircularProgress size={16} color="inherit" /> : <CheckCircle2 size={16} />
            }
          >
            {isAssigning ? 'Updating...' : 'Update Fee Structure'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
